import { haversineDistance, nearestNeighbor, sortSubchainsByOrderNum } from "../../../../utils/locationUtils";

/**
 * @author Bryan Lilly @date Oct 15, 2024
 * @param {Array} agms - these are the agms
 * 
 * 
 * @note The chain is the calculation from Launch to the current AGM No Subchains taken into account
 * 
 * 
 * @note !!!! This has been changed March 24, 2025 - Bryan Lilly
 *            Calculation now assumes the agms are sorted and we add the distance as we move on
 *            For edge cases where agm might be closer to launch than the previous agm, we will need to recalculate the chain 
 */
export const getAgmCalculatedChain = (agms) => {
    //assuming agms is organized from Launch to Receive
    //starting reference would be first element in array - everything is measured against that.
    
    let agmDataWithCalcChain = agms.map(agm => ({...agm}));
    // let launchAGM = agmDataWithCalcChain[0];
    // let launchLatAndLng = getLatAndLng(launchAGM);
    agmDataWithCalcChain[0].Calc_Chain = 0;
    let realCalcChain = 0;
    //loop through from second element to the last and calculate the distance using haversine distance
    for(let i = 1; i<agmDataWithCalcChain.length; i++){
        let currAgmLatAndLng = getLatAndLng(agmDataWithCalcChain[i]);
        let prevAgmLatAndLng = getLatAndLng(agmDataWithCalcChain[i-1]);

        // let chainDistance = haversineDistance(launchLatAndLng.lat, launchLatAndLng.lng, currAgmLatAndLng.lat, currAgmLatAndLng.lng);
        let chainDistance = haversineDistance(prevAgmLatAndLng.lat, prevAgmLatAndLng.lng, currAgmLatAndLng.lat, currAgmLatAndLng.lng);
        realCalcChain = realCalcChain + chainDistance;
        // agmDataWithCalcChain[i].Calc_Chain = chainDistance.toFixed(4)
        agmDataWithCalcChain[i].Calc_Chain = realCalcChain.toFixed(4);

    }
    return agmDataWithCalcChain;
}

/**
 * @author Bryan Lilly @date Oct 15, 2024
 * @param {Array} agms - these are the agms
 * @param {Array} subchains - these are the subchains
 * 
 * 
 * @note This is calculation with the subchains taken into account
 *       Need to sum the distances for accurate readings
 */
export const getAgmWithCalcChainUsingSubchain = (agms, subchains) => {
    //add agm/subchain key for easy access/sorting
    for(const agm of agms){
        agm.agm = true;
    }
    for(const subchain of subchains){
        subchain.subchain = true;
    }

    //to get seperate arrays of agm to agm with subchains just between those agms
    //assumes the agms are sorted by real chain or est chain so should be in order of launch to receive
    let returnArr = []; //array for returning agms with Calc_Chain
    let totalDistance = 0
    for(let i = 0; i < agms.length - 1; i++){
        if(i === 0){
            returnArr.push(agms[i]);
            returnArr[0].Calc_Chain = 0;
        }
        let currAgm = agms[i];
        let nextAgm = agms[i+1];
        let distance = 0;
        if(nextAgm){
            let currSubchains = subchains.filter(sc => sc.US_Site === currAgm.AGMID && sc.DS_Site === nextAgm.AGMID);
            let sortedSCArr = sortSubchainsByOrderNum([currAgm, ...currSubchains, nextAgm]);
            if (!sortedSCArr) {
                sortedSCArr = nearestNeighbor([currAgm, ...currSubchains, nextAgm]);
            } 
        
            sortedSCArr = sortedSCArr.filter(sc => sc.subchain === true);
            if(sortedSCArr.length){
                for(let j = 0; j < sortedSCArr.length; j++){
                    let marker1LatAndLng, marker2LatAndLng;
                    if(j === 0){
                        //calculate from agm to first subchain
                        marker1LatAndLng = getLatAndLng(currAgm);
                        marker2LatAndLng = getLatAndLng(sortedSCArr[j]);

                        let currDistance = haversineDistance(marker1LatAndLng.lat, marker1LatAndLng.lng, marker2LatAndLng.lat, marker2LatAndLng.lng);
                        distance += currDistance;
                    } 
                    if(j === sortedSCArr.length - 1) {
                        //if last element in array than calculate last subchain to ds agm 
                        marker1LatAndLng = getLatAndLng(sortedSCArr[j]);
                        marker2LatAndLng = getLatAndLng(nextAgm);
    
                        let currDistance = haversineDistance(marker1LatAndLng.lat, marker1LatAndLng.lng, marker2LatAndLng.lat, marker2LatAndLng.lng);
                        distance += currDistance;
                    } 
                    if(j + 1 < sortedSCArr.length) {
                        //calculate from previous subchain to next subchain
                        marker1LatAndLng = getLatAndLng(sortedSCArr[j]);
                        marker2LatAndLng = getLatAndLng(sortedSCArr[j+1]);
    
                        let currDistance = haversineDistance(marker1LatAndLng.lat, marker1LatAndLng.lng, marker2LatAndLng.lat, marker2LatAndLng.lng);
                        distance += currDistance;
                    }
                    // let currDistance = haversineDistance(marker1LatAndLng.lat, marker1LatAndLng.lng, marker2LatAndLng.lat, marker2LatAndLng.lng);
                    // distance += currDistance;
                } 
            } else {
                //there is no subchains so just calcualte the distance between the agms   
                let marker1LatAndLng = getLatAndLng(currAgm);
                let marker2LatAndLng = getLatAndLng(nextAgm);
    
                let currDistance = haversineDistance(marker1LatAndLng.lat, marker1LatAndLng.lng, marker2LatAndLng.lat, marker2LatAndLng.lng);
                distance += currDistance;
            }
            
            totalDistance += distance;
            nextAgm.Calc_Chain = totalDistance.toFixed(4);
        }
        returnArr.push(nextAgm);
    }
    return returnArr;
}


const getLatAndLng = (agm) => {
    if (!agm) return { lat: null, lng: null };

    //Julia Sept 8 2025: changed this from `agm.Real_Lat ?? agm.Est_Lat` etc, because this should also catch falsy values like "" that could occur when adding new rows (with empty input fields)
    //lat or lng being equal to "" can cause bugs in calculations - if lat or lng === "" it should be returned as null
    //downside of this is real values of 0 will also be returned as null, but we are not likely to have any locations at lat or lng of 0
    const lat = agm.Real_Lat || agm.Est_Lat || agm.lat || null;

    const lng = agm.Real_Lng || agm.Est_Lng || agm.lng || null;

    return { lat, lng };
};

/**
 * @param {Float} Real_Chain
 * @param {Float} Calculated_Chain
 * @returns {Float} Elong_Percent
 * @cateory Utils - Speed
 * @description This functions calculates the difference between real and calculated chain
 *              A positive means Real Chain is longer
 *              A negative means Real Chain is Shorter
 *              Used for calculating distances when we have to use Haversine, etc
 */
export const calculateElongationPercentage = (Real_Chain, Calculated_Chain) => {
    if(Real_Chain === 0) {
        throw new Error("Real Chain Distance Cannot be Zero");
    }
    return ((Real_Chain - Calculated_Chain) / Calculated_Chain) * 100;
}


/**
 * @author Bryan Lilly @date March 6, 2025
 * @param {Array} agms - these are the agms
 * 
 * 
 * @note Function to calculate the Elong_Percent for AGMs
 *       Elong_Percent is the percentage of the difference between the calc_chain and real_chain values
 *       negative elongation percent makes calculation of distance shorter to match Real Chain
 *       positive elongation percent makes calculation of distance longer to match Real Chain
 */
export const getElongPercents = (agms) => {
    
    let agmDataWithElongPercents = agms.map(agm => ({ ...agm }));

    for (let i = 0; i < agmDataWithElongPercents.length; i++) {

        if (agmDataWithElongPercents[i].Real_Chain && (agmDataWithElongPercents[i].Real_Chain !== 0 || agmDataWithElongPercents[i].Calc_Chain !== 0)) {
            try {
                const elongPercent = calculateElongationPercentage(
                    Number(agmDataWithElongPercents[i].Real_Chain - agmDataWithElongPercents[i - 1].Real_Chain),
                    Number(agmDataWithElongPercents[i].Calc_Chain - agmDataWithElongPercents[i - 1].Calc_Chain)
                );
                if (elongPercent instanceof Error) {
                    return elongPercent; //error handling in calling function
                }
                agmDataWithElongPercents[i].Elong_Percent = Number(elongPercent.toFixed(2));
            } catch (error) {
                return error;
            }
        }
    }
    return agmDataWithElongPercents;
}

/**
 * @author Bryan Lilly @date March 12, 2025
 * @param {Array} agms - these are the agms
 * 
 * 
 * @note 
 */
export const getRealChainDelta = (agmData, currentRow) => {

    if (!agmData || !currentRow) return 0;
    if (currentRow.Real_Chain === 0) return 0;


    const index = agmData.indexOf(currentRow);
    if (index <= 0) return null;

    const prevRow = agmData[index - 1];
    if (!prevRow || prevRow.Real_Chain == null || currentRow.Real_Chain == null) return null;

    const delta = currentRow.Real_Chain - prevRow.Real_Chain;

    return Number.isFinite(delta) ? delta.toFixed(2) : null;
};

/**
 * @author Bryan Lilly @date April 11, 2025
 * @param {Array} agms - these are the agms
 * 
 * 
 * @note 
 */

let sortedPathCache = {}; // module/global cache for nearest neighbour calcs - for US and DS distance calcs

export const getUSDistance = (agms, subchains, scRow, haveSubchainsChanged = false) => {

    if(!agms || !subchains || !scRow) return;

    //if current subchain doesn't exist in this segment, must have been a change in the subchains - rebuild segment in cache
    const cacheKey = `${scRow.US_Site}_${scRow.DS_Site}`;
    const currentSegment = sortedPathCache[cacheKey]
    const subchainInSegment = currentSegment?.find(item => (item?.SCID && scRow.SCID) && item.SCID === scRow.SCID)

    // Compute + cache sorted path only once
    if (!currentSegment || !subchainInSegment || haveSubchainsChanged) {

        const dataWithKeys = setSCAGMKey(agms, subchains);
        const agmsWithKeys = dataWithKeys.agms;
        const subchainsWithKeys = dataWithKeys.subchains;

        const usAgm = agmsWithKeys.find(agm => agm.AGMID === scRow.US_Site);
        const dsAgm = agmsWithKeys.find(agm => agm.AGMID === scRow.DS_Site);

        const relevantSubchains = subchainsWithKeys.filter(sc => sc.US_Site === scRow.US_Site && sc.DS_Site === scRow.DS_Site);

        let sorted = sortSubchainsByOrderNum([usAgm, ...relevantSubchains, dsAgm]);
        if(!sorted){
            sorted = nearestNeighbor([usAgm, ...relevantSubchains, dsAgm]);
        }             
        sortedPathCache[cacheKey] = sorted;
    }

    const sortedSCArr = sortedPathCache[cacheKey];

    // Walk through sorted path to the current subchain
    let distance = 0;

    for (let i = 0; i < sortedSCArr.length - 1; i++) {
        const pointA = getLatAndLng(sortedSCArr[i]);
        const pointB = getLatAndLng(sortedSCArr[i + 1]);
        distance += haversineDistance(pointA.lat, pointA.lng, pointB.lat, pointB.lng);

        // Stop when we hit the current subchain
        if (sortedSCArr[i + 1]?.subchain && sortedSCArr[i + 1].SC_Name === scRow.SC_Name) break;
    }

    return distance;
}

/**
 * @author Bryan Lilly @date April 11, 2025
 * @param {Array} agms - these are the agms
 * 
 * 
 * @note 
 */
export const getDSDistance = (agms, subchains, scRow, haveSubchainsChanged = false) => {

    if(!agms || !subchains || !scRow) return;

    //if current subchain doesn't exist in this segment, must have been a change in the subchains - rebuild segment in cache
    const cacheKey = `${scRow.US_Site}_${scRow.DS_Site}`;
    const currentSegment = sortedPathCache[cacheKey]
    const subchainInSegment = currentSegment?.find(item => (item?.SCID && scRow.SCID) && item.SCID === scRow.SCID)

    if (!currentSegment || !subchainInSegment || haveSubchainsChanged) {
        const dataWithKeys = setSCAGMKey(agms, subchains);
        agms = dataWithKeys.agms;
        subchains = dataWithKeys.subchains;

        const usAgm = agms.find(agm => agm.AGMID === scRow.US_Site);
        const dsAgm = agms.find(agm => agm.AGMID === scRow.DS_Site);

        const relevantSubchains = subchains.filter(sc => sc.US_Site === scRow.US_Site && sc.DS_Site === scRow.DS_Site);

        let sorted = sortSubchainsByOrderNum([usAgm, ...relevantSubchains, dsAgm]);
        if(!sorted){
            sorted = nearestNeighbor([usAgm, ...relevantSubchains, dsAgm]);
        } 
        sortedPathCache[cacheKey] = sorted;
    }

    const sortedSCArr = sortedPathCache[cacheKey];
    const startIndex = sortedSCArr.findIndex(sc => sc?.subchain && sc.SC_Name === scRow.SC_Name);

    if (startIndex === -1) return 0;

    let distance = 0;
    for (let i = startIndex; i < sortedSCArr.length - 1; i++) {
        const point1 = getLatAndLng(sortedSCArr[i]);
        const point2 = getLatAndLng(sortedSCArr[i + 1]);
        distance += haversineDistance(point1.lat, point1.lng, point2.lat, point2.lng);

        if (sortedSCArr[i + 1]?.agm) break;
    }

    return distance;
}

const setSCAGMKey = (agms, subchains) => {
    //add agm/subchain key for easy access/sorting
    for(const agm of agms){
        agm.agm = true;
    }
    for(const subchain of subchains){
        subchain.subchain = true;
    }

    return {agms, subchains}
}